// Updated ClaimListContext to support EmrPaymentEraDTO
import React, { createContext, useContext, useState, useCallback } from 'react';
import {
    EmrClaim,
    ClaimFilterCriteria,
    EmrPaymentDTO,
    EmrPaymentEraDTO
} from '../types';
import axiosApexInstance from '../auth/axiosApexInstance';
import { showToastError, showToastSuccess } from '../utils/toastUtils';

interface ClaimListContextType {
    claims: EmrClaim[];
    criteria: ClaimFilterCriteria;
    setCriteria: (c: ClaimFilterCriteria) => void;
    fetchClaims: () => Promise<void>;
    setClaims: React.Dispatch<React.SetStateAction<EmrClaim[]>>;
    didInitialSearch: boolean;
    setDidInitialSearch: (value: boolean) => void;

    payments: EmrPaymentDTO[];
    fetchPayments: () => Promise<void>;
    setPayments: React.Dispatch<React.SetStateAction<EmrPaymentDTO[]>>;

    eraPayments: EmrPaymentEraDTO[];
    fetchEraPayments: () => Promise<void>;
    setEraPayments: React.Dispatch<React.SetStateAction<EmrPaymentEraDTO[]>>;
}

const ClaimListContext = createContext<ClaimListContextType | undefined>(undefined);

export const ClaimListProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [claims, setClaims] = useState<EmrClaim[]>([]);
    const [criteria, setCriteria] = useState<ClaimFilterCriteria>({});
    const [didInitialSearch, setDidInitialSearch] = useState(false);

    const [payments, setPayments] = useState<EmrPaymentDTO[]>([]);
    const [eraPayments, setEraPayments] = useState<EmrPaymentEraDTO[]>([]);

    const fetchClaims = useCallback(async () => {
        try {
            // Check if CPT filters are active
            const hasCptFilters = criteria.pos || 
                                 (criteria.cptCodes && criteria.cptCodes.length > 0) || 
                                 criteria.cptGroupKey;
            
            let res;
            if (hasCptFilters) {
                // Use CPT filtering API
                res = await axiosApexInstance.post('/task/emr/claim/search/cpt-filter', criteria);
            } else {
                // Use regular search API
                res = await axiosApexInstance.post('/task/emr/claim/search', criteria);
            }
            setClaims(res.data);
            showToastSuccess('Claims loaded');
        } catch (err) {
            console.error('Error fetching claims:', err);
            showToastError('Failed to load claims');
        }
    }, [criteria]);

    const fetchPayments = useCallback(async () => {
        try {
            const res = await axiosApexInstance.get('/task/emr/payments');
            setPayments(res.data);
            showToastSuccess('Payments loaded');
        } catch (err) {
            console.error('Error fetching payments:', err);
            showToastError('Failed to load payments');
        }
    }, []);

    const fetchEraPayments = useCallback(async () => {
        try {
            const twoYearsAgo = new Date();
            twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
            const endOfYear = new Date(new Date().getFullYear(), 11, 31, 23, 59, 59);

            const res = await axiosApexInstance.get('/task/emr/emr-era-payments', {
                params: {
                    from: twoYearsAgo.toISOString(),
                    to: endOfYear.toISOString(),
                },
            });
            setEraPayments(res.data);
            showToastSuccess('ERA Payments loaded');
        } catch (err) {
            console.error('Error fetching ERA payments:', err);
            showToastError('Failed to load ERA payments');
        }
    }, []);

    return (
        <ClaimListContext.Provider value={{
            claims, setClaims, criteria, setCriteria, fetchClaims,
            didInitialSearch, setDidInitialSearch,
            payments, fetchPayments, setPayments,
            eraPayments, fetchEraPayments, setEraPayments
        }}>
            {children}
        </ClaimListContext.Provider>
    );
};

export const useClaimList = (): ClaimListContextType => {
    const ctx = useContext(ClaimListContext);
    if (!ctx) throw new Error('useClaimList must be used within a ClaimListProvider');
    return ctx;
};
